import React, { useMemo } from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import TechnikaServiceApi from '../api/JsdhNotifikaceService/service/TechnikaService';
import JsdhNotifikaceService from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import type { Udalost, Technika, PoplachTechnikaResponse } from '../api/JsdhNotifikaceService/generated';
import TechnikaFoto from './TechnikaFoto';

interface TechnikaWrapperProps {
    aktivniPoplach: Udalost;
    deviceToken?: string;
}

const TechnikaWrapper: React.FC<TechnikaWrapperProps> = ({ aktivniPoplach, deviceToken }) => {
    const { data: allTechnika } = useQuery<Technika[]>({
        queryKey: ['technika', 'active', deviceToken],
        queryFn: () =>
            deviceToken
                ? TechnikaServiceApi.getActiveTechnikaWithDeviceToken(deviceToken)
                : TechnikaServiceApi.getActiveTechnika(),
        // Kiosk bez obsluhy — viz DashboardPage: bez intervalu zustane seznam po jednom
        // selhanem nacteni prazdny az do rucniho reloadu stranky.
        refetchInterval: 15000,
        staleTime: 10000,
        refetchOnWindowFocus: false,
    });

    // Query na detaily techniky s info o zdroji (KOPIS/VELITEL)
    const { data: technikaSources } = useQuery<PoplachTechnikaResponse[]>({
        queryKey: ['udalost-technika', aktivniPoplach.id, deviceToken],
        queryFn: () => JsdhNotifikaceService.getUdalostTechnika(aktivniPoplach.id, deviceToken),
        enabled: Boolean(aktivniPoplach.id),
        refetchInterval: 5000,
        refetchOnWindowFocus: false,
        staleTime: 4000,
    });

    const sourceMap = useMemo(() => {
        const map = new Map<number, PoplachTechnikaResponse>();
        technikaSources?.forEach(t => map.set(t.technikaId, t));
        return map;
    }, [technikaSources]);

    // Filtrovat techniku podle ID z poplach_technika (napárováno na backendu)
    const matchedTechnika = useMemo(() => {
        if (!allTechnika || !aktivniPoplach.technikaIds?.length) return [];

        return allTechnika.filter(t => aktivniPoplach.technikaIds!.includes(t.id));
    }, [allTechnika, aktivniPoplach.technikaIds]);

    if (matchedTechnika.length === 0) {
        return (
            <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.85rem', mb: 0.5 }}>
                    Technika
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
                    Žádná technika nebyla spárována
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            p: 1,
        }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {matchedTechnika.map(t => {
                    const source = sourceMap.get(t.id);
                    return (
                    <Paper
                        key={t.id}
                        elevation={1}
                        sx={{
                            p: 0.75,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1.5,
                            flex: '1 1 calc(33.333% - 8px)',
                            maxWidth: 'calc(33.333% - 6px)',
                            minWidth: 200,
                        }}
                    >
                        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                            <Typography sx={{ fontSize: '3rem', fontWeight: 900, color: 'error.main', wordBreak: 'break-word', lineHeight: 1.1 }}>
                                {t.volaciZnak}
                            </Typography>
                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3, wordBreak: 'break-word' }}>
                                {t.nazev}
                            </Typography>
                            {t.spz && (
                                <Typography sx={{ fontSize: '1rem', color: 'text.disabled', lineHeight: 1.3, mt: 0.5 }}>
                                    {t.spz}
                                </Typography>
                            )}
                            {source && (
                                <Chip
                                    size="small"
                                    label={
                                        source.zdroj === 'KOPIS'
                                            ? 'KOPIS'
                                            : source.potvrzenoKopis
                                                ? `Velitel ✓ KOPIS`
                                                : 'Velitel'
                                    }
                                    sx={{
                                        mt: 0.5,
                                        fontWeight: 700,
                                        fontSize: '0.7rem',
                                        color: '#fff',
                                        bgcolor: source.zdroj === 'KOPIS'
                                            ? 'info.main'
                                            : source.potvrzenoKopis
                                                ? 'success.main'
                                                : 'warning.main',
                                    }}
                                />
                            )}
                        </Box>
                        <TechnikaFoto
                            technikaId={t.id}
                            maFotografii={t.maFotografii}
                            alt={t.nazev}
                            deviceToken={deviceToken}
                            objectFit="contain"
                            ikonaFontSize={64}
                            sx={{ width: '60%', aspectRatio: '4/3' }}
                        />
                    </Paper>
                    );
                })}
            </Box>
        </Box>
    );
};

export default React.memo(TechnikaWrapper);
